import React, { useState } from "react";
import CollapseSection from "./CollapseSection";
import { collectionPageSettings } from "../../../themes/collectionPageSettings";
import { CATEGORY_COLORS, CATEGORY_TEXTS } from "../../../themes/pageCustomization";

const GROUPS = [
  {
    id: "page",
    title: "Page",
    text: "Background, title, breadcrumbs, and the product count.",
    keys: [
      "collectionBackground",
      "collectionHeadingColor",
      "collectionTextColor",
      "collectionTitleAlign",
      "collectionTitleSize",
      "collectionShowBreadcrumbs",
      "collectionShowDescription",
      "collectionShowCount",
    ],
  },
  {
    id: "layout",
    title: "Layout",
    text: "Columns, photo shape, filter, sort, and paging.",
    keys: [
      "collectionColumns",
      "collectionColumnsMobile",
      "collectionImageRatio",
      "collectionImageFit",
      "collectionPerPage",
      "collectionPagination",
      "collectionFilterStyle",
      "collectionShowFilter",
      "collectionShowSort",
    ],
  },
  {
    id: "card",
    title: "Product card",
    text: "Name, price, badges, and the quick add button.",
    keys: [
      "collectionNameColor",
      "collectionPriceColor",
      "collectionOldPriceColor",
      "collectionVendorColor",
      "collectionShowVendor",
      "collectionShowSwatches",
      "collectionShowHoverImage",
      "collectionShowWishlist",
      "collectionShowSaleBadge",
      "collectionSaleLabel",
      "collectionSaleBackground",
      "collectionSaleText",
      "collectionShowSoldOut",
      "collectionSoldLabel",
      "collectionSoldBackground",
      "collectionSoldText",
      "collectionShowQuickAdd",
      "collectionAddLabel",
      "collectionChooseLabel",
      "collectionQuickBackground",
      "collectionQuickText",
      "collectionQuickBorder",
    ],
  },
  {
    id: "more-colors",
    title: "More colors",
    text: "Breadcrumbs, filter, count, chips, page numbers, and Load more.",
    keys: CATEGORY_COLORS.map((color) => color.key),
  },
  {
    id: "words",
    title: "Words",
    text: "Every label on the page, in your own words.",
    keys: CATEGORY_TEXTS.map((item) => item.key),
  },
  {
    id: "index",
    title: "All collections",
    text: "The page that lists every collection.",
    keys: [
      "collectionIndexColumns",
      "collectionIndexColumnsMobile",
      "collectionIndexShowCount",
      "collectionIndexCaption",
      "collectionIndexOverlay",
    ],
  },
];

const SAMPLES = [
  { name: "Sample shirt", brand: "Polo", price: "Rs. 1,200", old: "Rs. 1,500", sale: true, colors: ["#dc2626", "#16a34a"] },
  { name: "Black bag", brand: "Asd", price: "Rs. 22", sold: true, colors: [] },
  { name: "Blue shirt", brand: "ZnZ", price: "Rs. 600", colors: ["#2563eb"] },
  { name: "Travis shirt", brand: "Polo", price: "Rs. 1,000", colors: ["#dc2626", "#16a34a"] },
];

export function CollectionSettings({ fields, settings, renderField }) {
  const [openId, setOpenId] = useState("page");
  const byKey = Object.fromEntries((fields || []).map((field) => [field.key, field]));
  const look = collectionPageSettings(settings || {});
  const fallbacks = {
    collectionBackground: look.background,
    collectionHeadingColor: look.headingColor,
    collectionTextColor: look.textColor,
    collectionNameColor: look.nameColor,
    collectionPriceColor: look.priceColor,
    collectionOldPriceColor: look.oldPriceColor,
    collectionVendorColor: look.vendorColor,
    collectionSaleBackground: look.saleBackground,
    collectionSaleText: look.saleText,
    collectionSoldBackground: look.soldBackground,
    collectionSoldText: look.soldText,
    collectionQuickBackground: look.quickBackground,
    collectionQuickText: look.quickText,
    collectionQuickBorder: look.quickBorder,
    collectionIndexCaption: look.indexCaption,
    collectionIndexOverlay: look.indexOverlay,
  };
  const toggle = (id) => setOpenId((current) => (current === id ? null : id));

  return (
    <div className="te-footer-settings">
      {GROUPS.map((group) => {
        const groupFields = group.keys.map((key) => byKey[key]).filter(Boolean);
        if (groupFields.length === 0) return null;
        return (
          <CollapseSection
            key={group.id}
            title={group.title}
            text={group.text}
            open={openId === group.id}
            onToggle={() => toggle(group.id)}
          >
            {groupFields.map((field) => {
              const nextField = fallbacks[field.key] ? { ...field, fallback: fallbacks[field.key] } : field;
              return <React.Fragment key={field.key}>{renderField(nextField)}</React.Fragment>;
            })}
          </CollapseSection>
        );
      })}
    </div>
  );
}

export function CollectionPagePreview({ settings }) {
  const look = collectionPageSettings(settings || {});
  const ratio = look.imageRatio === "square" ? "1 / 1" : look.imageRatio === "landscape" ? "4 / 3" : "3 / 4";
  const titleSize = look.titleSize === "small" ? 22 : look.titleSize === "large" ? 34 : 28;
  return (
    <div className="te-collection-preview" aria-hidden="true">
      <div style={{ background: look.background, color: look.headingColor, padding: "18px 16px 28px" }}>
        <div style={{ color: look.indexCaption, fontSize: 11, marginBottom: 8 }}>All collections</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${look.indexColumns}, minmax(0, 1fr))`,
            gap: 8,
            marginBottom: 18,
          }}
        >
          {["Shirts", "Pants", "Bags"].slice(0, look.indexColumns).map((name) => (
            <div key={name} style={{ position: "relative", aspectRatio: ratio, background: "#d6d3d1", overflow: "hidden" }}>
              <span
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  padding: "16px 8px 8px",
                  color: look.indexCaption,
                  background: `linear-gradient(transparent, ${look.indexOverlay})`,
                  fontSize: 13,
                }}
              >
                {name}
                {look.indexShowCount ? <small style={{ display: "block", fontWeight: 400 }}>5 products</small> : null}
              </span>
            </div>
          ))}
        </div>

        {look.showBreadcrumbs && (
          <div style={{ color: look.textColor, fontSize: 12, textAlign: look.titleAlign }}>Home / Collections / Shirts</div>
        )}
        <h3 style={{ margin: "8px 0 0", fontWeight: 500, fontSize: titleSize, textAlign: look.titleAlign, color: look.headingColor }}>
          Shirts
        </h3>
        {look.showDescription && (
          <p style={{ margin: "8px 0 0", color: look.textColor, fontSize: 13, textAlign: look.titleAlign }}>
            A short description of this collection.
          </p>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, margin: "14px 0", fontSize: 12 }}>
          {look.showFilter ? (
            <span style={{ border: `1px solid ${look.headingColor}`, padding: "6px 10px" }}>
              {look.filterStyle === "sidebar" ? "Left filter" : "Filter"}
            </span>
          ) : <span />}
          <span style={{ color: look.textColor }}>
            {look.showCount ? "4 products" : ""}
            {look.showSort ? " · Sort by Featured" : ""}
          </span>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${look.columns}, minmax(0, 1fr))`,
            gap: 10,
          }}
        >
          {SAMPLES.map((item) => (
            <div key={item.name}>
              <div style={{ position: "relative", aspectRatio: ratio, background: "#ececec" }}>
                {look.showSaleBadge && item.sale && (
                  <span style={{ position: "absolute", top: 6, left: 6, background: look.saleBackground, color: look.saleText, fontSize: 11, padding: "3px 6px" }}>
                    {look.saleLabel}
                  </span>
                )}
                {look.showSoldOut && item.sold && (
                  <span style={{ position: "absolute", top: 6, left: 6, background: look.soldBackground, color: look.soldText, fontSize: 11, padding: "3px 6px" }}>
                    {look.soldLabel}
                  </span>
                )}
                {look.showQuickAdd && (
                  <div
                    style={{
                      position: "absolute",
                      left: 6,
                      right: 6,
                      bottom: 6,
                      textAlign: "center",
                      fontSize: 11,
                      padding: "6px 4px",
                      background: look.quickBackground,
                      color: look.quickText,
                      border: `1px solid ${look.quickBorder}`,
                    }}
                  >
                    {item.name === "Travis shirt" ? look.chooseLabel : look.addLabel}
                  </div>
                )}
              </div>
              {look.showVendor && <div style={{ color: look.vendorColor, fontSize: 11, marginTop: 6 }}>{item.brand}</div>}
              <div style={{ color: look.nameColor, fontSize: 13, marginTop: 2 }}>{item.name}</div>
              <div style={{ color: look.priceColor, fontSize: 12 }}>
                {item.price} {item.old ? <s style={{ color: look.oldPriceColor }}>{item.old}</s> : null}
              </div>
              {look.showSwatches && item.colors.length > 0 && (
                <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                  {item.colors.map((color) => (
                    <span key={color} style={{ width: 10, height: 10, borderRadius: "50%", background: color, display: "inline-block" }} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: look.textColor }}>
          {look.pagination === "more" ? "Load more" : "1  2  3"}
        </div>
      </div>
    </div>
  );
}
